'use client'
import { useEffect, useRef, useState } from 'react'
import createGlobe from 'cobe'
import axios from 'axios'
import { supabase } from '@/services/supabase'
import { Users, MapPin, Globe } from 'lucide-react'

type Location = { latitude: number; longitude: number; city?: string; country?: string }

function useCountUp(value: number, duration = 800) {
  const [display, setDisplay] = useState(0)
  useEffect(() => {
    const start = display
    const diff = value - start
    const startTime = performance.now()
    const tick = (t: number) => {
      const progress = Math.min((t - startTime) / duration, 1)
      setDisplay(Math.floor(start + diff * progress))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [value])
  return display
}

export default function RealtimeGlobe() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [myLocation, setMyLocation] = useState<Location | null>(null)
  const [isRotating, setIsRotating] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const [hoveredLocation, setHoveredLocation] = useState<Location | null>(null)
  const pointerInteracting = useRef<number | null>(null)
  const pointerInteractionMovement = useRef(0)
  const phiRef = useRef(0)
  const velocityRef = useRef(0)
  const globeRef = useRef<any>(null)

  // detect location
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      pos => {
        setMyLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          city: 'Unknown',
          country: 'Unknown'
        })
      },
      async () => {
        try {
          let { data } = await axios.get('https://ipapi.co/json/')
          if (!data?.city || !data?.country_name) {
            const res = await axios.get('https://ipwho.is/')
            data = res.data
          }
          setMyLocation({
            latitude: data.latitude,
            longitude: data.longitude,
            city: data.city || data.locality || 'Unknown',
            country: data.country_name || data.country || 'Unknown'
          })
        } catch {
          setMyLocation({
            latitude: Math.random() * 180 - 90,
            longitude: Math.random() * 360 - 180,
            city: 'Unknown',
            country: 'Unknown'
          })
        }
      }
    )
  }, [])

  // realtime broadcast
  useEffect(() => {
    if (!myLocation) return
    const channel = supabase.channel('kyrosvisitors')
    channel
      .on('broadcast', { event: 'locations' }, payload => setLocations(payload.payload))
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return
        channel.httpSend('locations', [
          {
            latitude: myLocation.latitude,
            longitude: myLocation.longitude,
            city: myLocation.city,
            country: myLocation.country
          }
        ])
      })
    return () => {
      supabase.removeChannel(channel)
    }
  }, [myLocation])

  // globe render
  useEffect(() => {
    if (!canvasRef.current) return
    
    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: 800,
      height: 800,
      phi: 0,
      theta: 0.3,
      dark: 1,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.2, 0.2, 0.2],
      markerColor: [0.9, 0.5, 0.15],
      glowColor: [0.4, 0.2, 0.05],
      markers: [],
      opacity: 0.8,
      onRender(state) {
        // Update markers
        state.markers = locations.map(loc => ({
          location: [loc.latitude, loc.longitude],
          size:
            myLocation &&
            loc.latitude === myLocation.latitude &&
            loc.longitude === myLocation.longitude
              ? 0.1
              : 0.05
        }))
        
        // Smooth auto-rotation
        if (isRotating && !pointerInteracting.current) {
          phiRef.current += 0.003 // Natural slow rotation
        }
        
        // Apply velocity (momentum after drag)
        if (!pointerInteracting.current && !isRotating && velocityRef.current !== 0) {
          phiRef.current += velocityRef.current
          // Decay velocity for smooth stop
          velocityRef.current *= 0.95
          if (Math.abs(velocityRef.current) < 0.0001) {
            velocityRef.current = 0
          }
        }
        
        state.phi = phiRef.current
      }
    })
    globeRef.current = globe
    return () => globe.destroy?.()
  }, [locations, myLocation, isRotating])

  // Helper function to convert lat/lng to screen coordinates
  const projectToScreen = (lat: number, lng: number, phi: number, theta: number) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    
    // Simple projection approximation
    const adjustedLng = lng - (phi * 180 / Math.PI)
    const x = (adjustedLng + 180) / 360
    const y = (90 - lat) / 180
    
    const rect = canvas.getBoundingClientRect()
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const radius = Math.min(rect.width, rect.height) / 2
    
    const screenX = centerX + (x - 0.5) * radius * 2 * Math.cos(theta)
    const screenY = centerY + (y - 0.5) * radius * 2
    
    return { x: screenX, y: screenY }
  }

  // Handle hover to show location
  const handleCanvasHover = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't show hover while dragging
    if (isDragging) {
      setHoveredLocation(null)
      return
    }
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const hoverX = e.clientX - rect.left
    const hoverY = e.clientY - rect.top
    
    // Check if hover is near any location marker
    const threshold = 30 // pixels
    for (const loc of locations) {
      const projected = projectToScreen(loc.latitude, loc.longitude, phiRef.current, 0.3)
      if (projected) {
        const distance = Math.sqrt(
          Math.pow(hoverX - projected.x, 2) + Math.pow(hoverY - projected.y, 2)
        )
        if (distance < threshold) {
          setHoveredLocation(loc)
          return
        }
      }
    }
    
    // If no marker is hovered, clear
    setHoveredLocation(null)
  }

  // Handle click to toggle rotation
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't do anything if user was dragging
    if (Math.abs(velocityRef.current) > 0.01) return
    
    // Toggle rotation pause/resume
    setIsRotating(!isRotating)
  }

  // derived stats
  const uniqueCities = Array.from(new Set(locations.map(l => l.city).filter(Boolean)))
  const uniqueCountries = Array.from(new Set(locations.map(l => l.country).filter(Boolean)))

  const peopleCount = useCountUp(locations.length)
  const cityCount = useCountUp(uniqueCities.length)
  const countryCount = useCountUp(uniqueCountries.length)

  return (
    <div
      style={{
        width: '100%',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: 'white',
        padding: '2rem 1rem'
      }}
    >
      <h1 style={{ fontSize: '2.2rem', fontWeight: 700, marginBottom: '1.5rem' }}>
        Your city glows when you
        <br />
        join the flow.
      </h1>

      {/* Live stats */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.5rem',
        fontSize: '1rem',
        color: '#ccc',
        marginBottom: '1rem',
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '300px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={18} style={{ color: '#E67E22' }} />
          <span style={{ color: '#E67E22', fontWeight: 700 }}>{peopleCount}</span>{' '}
          {peopleCount === 1 ? 'person' : 'people'} live now!
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MapPin size={18} style={{ color: '#E67E22' }} />
          <span style={{ color: '#E67E22', fontWeight: 700 }}>{cityCount}</span>{' '}
          {cityCount === 1 ? 'city' : 'cities'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Globe size={18} style={{ color: '#E67E22' }} />
          <span style={{ color: '#E67E22', fontWeight: 700 }}>{countryCount}</span>{' '}
          {countryCount === 1 ? 'country' : 'countries'}
        </div>
      </div>

      <div style={{ marginTop: 30, position: 'relative' }}>
        <canvas
          ref={canvasRef}
          style={{
            width: 400,
            height: 400,
            aspectRatio: 1,
            borderRadius: '50%',
            boxShadow: isDragging 
              ? '0 0 70px rgba(230,126,34,0.6), 0 0 100px rgba(230,126,34,0.3)' 
              : '0 0 50px rgba(230,126,34,0.4)',
            cursor: isDragging ? 'grabbing' : 'grab',
            transition: 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'transform',
            userSelect: 'none'
          }}
          onPointerDown={(e) => {
            pointerInteracting.current = e.clientX
            setIsDragging(true)
            velocityRef.current = 0 // Stop momentum when starting drag
            setHoveredLocation(null) // Clear hover when dragging starts
          }}
          onPointerUp={() => {
            pointerInteracting.current = null
            setIsDragging(false)
          }}
          onPointerOut={() => {
            pointerInteracting.current = null
            setIsDragging(false)
            setHoveredLocation(null) // Clear hover when mouse leaves
          }}
          onMouseMove={(e) => {
            if (pointerInteracting.current !== null) {
              const delta = (e.clientX - pointerInteracting.current) * 0.005 // Smooth sensitivity
              phiRef.current += delta
              velocityRef.current = delta * 0.8 // Track velocity for momentum
              pointerInteracting.current = e.clientX
            } else {
              // Check for hover when not dragging
              handleCanvasHover(e)
            }
          }}
          onTouchMove={(e) => {
            if (pointerInteracting.current !== null && e.touches[0]) {
              const delta = (e.touches[0].clientX - pointerInteracting.current) * 0.005
              phiRef.current += delta
              velocityRef.current = delta * 0.8
              pointerInteracting.current = e.touches[0].clientX
            }
          }}
          onClick={handleCanvasClick}
        />
        
        {/* Hovered Location Info */}
        {hoveredLocation && (
          <div
            style={{
              position: 'absolute',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(230, 126, 34, 0.95)',
              color: 'white',
              padding: '10px 16px',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(230, 126, 34, 0.4)',
              zIndex: 10,
              minWidth: '180px',
              textAlign: 'center',
              pointerEvents: 'none',
              transition: 'opacity 0.2s ease'
            }}
          >
            📍 {hoveredLocation.city || 'Unknown'}, {hoveredLocation.country || 'Unknown'}
          </div>
        )}

        {/* Pause/Play indicator */}
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(230, 126, 34, 0.9)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: 600,
            opacity: isRotating ? 0 : 1,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none'
          }}
        >
          ⏸ Paused (click to resume)
        </div>
      </div>

      {/* Offline message below globe */}
      {peopleCount === 0 && (
        <p style={{ 
          marginTop: 20, 
          color: '#aaa', 
          fontSize: '0.95rem',
          fontStyle: 'italic'
        }}>
          No one's live right now — but the rhythm never stops.
        </p>
      )}
    </div>
  )
}